<template>
    <div>
        <div class="body">
            <l-input-text
                ref="input-name"
                v-model="element.name"
                type="text"
                :readonly="element.frozen || false"
                :label="''"
                :placeholder="$t(`learning.admin.element.details.placeholder.name`)"
                data-test-id="element-edit-name"
            ></l-input-text>
            <l-input-text
                ref="input-description"
                v-model="element.description"
                type="textarea"
                :optional="true"
                :readonly="element.frozen || false"
                :show-text-format-hint="true"
                :label="''"
                :placeholder="$t(`learning.admin.element.details.placeholder.description`)"
                data-test-id="element-edit-description"
            ></l-input-text>
            <l-input-text
                ref="input-url"
                v-model="element.location.url"
                type="url"
                :optional="element.location.type !== 'link'"
                :readonly="element.frozen || false"
                :label="''"
                :placeholder="$t(`learning.admin.element.details.placeholder.url`)"
                :validator="isValidURL"
                :error-message="$t(`learning.admin.element.details.error.url`)"
                data-test-id="element-edit-link"
            ></l-input-text>
            <l-input-file
                v-show="!element.frozen"
                v-model="file"
                :placeholder="
                    filePlaceholder || $t(`learning.admin.element.details.placeholder.file`)
                "
                :accept="getAcceptFileTypes(element.type)"
                :file-uploading="fileUploading"
                data-test-id="element-file"
                @change="onFileChange"
            ></l-input-file>
            <div v-show="!element.frozen">
                <hr />
                <label for="element-open-enabled-check">
                    <input
                        id="element-open-enabled-check"
                        v-model="element.open.enabled"
                        type="checkbox"
                    />
                    <span class="color-dark">
                        {{ $t(`learning.admin.element.details.open.markOpen`) }}
                    </span>
                    <span
                        v-b-popover.hover="{
                            content: $t(`learning.admin.element.details.open.markOpenPopover`),
                        }"
                        class="l-default-margin"
                    >
                        <i class="fal fa-question-circle"></i>
                    </span>
                </label>
            </div>
            <hr />
            <div v-show="element.open.enabled">
                <label for="element-targetMode-all-check">
                    <input
                        id="element-targetMode-all-check"
                        v-model="targetMode"
                        type="radio"
                        value="all"
                        data-test-id="learning-path-targeting-participants-all"
                    />

                    <span class="color-dark targeting">{{
                        $t("learning.admin.element.details.open.targeting.participants.label.all")
                    }}</span> </label
                ><br />
                <label for="element-targetMode-selectedParticipants-check">
                    <input
                        id="element-targetMode-selectedParticipants-check"
                        v-model="targetMode"
                        type="radio"
                        value="selectedParticipants"
                    />

                    <span class="color-dark targeting">{{
                        $t(
                            "learning.admin.element.details.open.targeting.participants.label.selected",
                        )
                    }}</span>
                </label>

                <br />

                <div v-show="targetMode === 'selectedParticipants'">
                    <l-input-typeahead
                        :label="''"
                        :data="participants"
                        :on-select="typeaheadParticipantsOnSelect"
                        :on-input-change="typeaheadParticipantsOnInputChange"
                        :placeholder="
                            $t(
                                `learning.admin.element.details.open.targeting.participants.placeholder.selected`,
                            )
                        "
                    >
                    </l-input-typeahead>

                    <span
                        v-for="(participant, index) in selectedParticipants"
                        v-show="selectedParticipants.length"
                        :key="participant._id"
                    >
                        <span
                            class="label label-default"
                            tabindex="0"
                            @click="removeSelectedParticipant(index)"
                            @keyup.enter="removeSelectedParticipant(index)"
                        >
                            {{ participant.name }} <i class="fa fa-remove"></i>
                        </span>
                    </span>
                </div>
            </div>
        </div>

        <br />

        <div class="footer">
            <div
                v-b-popover.hover.topleft="{
                    content: $t('learning.admin.element.details.tooltip.deleteDisabled'),
                    disabled:
                        !showDeleteBtn || !(element.usedInPaths && element.usedInPaths.length),
                }"
            >
                <button
                    v-show="showDeleteBtn"
                    class="btn btn-default btn-raised"
                    :disabled="
                        disableSubmit || Boolean(element.usedInPaths && element.usedInPaths.length)
                    "
                    data-test-id="element-delete"
                    @click="deleteElement"
                >
                    <i class="fa fa-remove"></i>
                    {{ $t("learning.admin.element.details.btn.delete") }}
                </button>
            </div>
            <div>
                <button class="btn" data-test-id="element-cancel" @click="cancel">
                    {{ $t("learning.admin.element.details.btn.back") }}
                </button>
                <button
                    v-show="!element.frozen"
                    class="btn btn-primary btn-raised"
                    :disabled="disableSubmit || fileUploading"
                    data-test-id="element-submit"
                    @click="submit"
                >
                    {{ $t("learning.admin.element.details.btn.save") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import isURL from "is-url";
import { mapGetters } from "vuex";

import teamPicture from "@/img/icons/team-icon.png";
import getFileNameFromAWSKey from "@/utils/get-file-name-from-aws-key";

import LInputFile from "@/components/LInputFile";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";

export default {
    components: {
        LInputFile,
        LInputText,
        LInputTypeahead,
    },
    props: {
        initialData: {
            type: Object,
            required: true,
        },
        showDeleteBtn: {
            type: Boolean,
            default: true,
        },
        disableSubmit: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            element: { ...this.initialData },
            file: "",
            fileUploading: false,
        };
    },
    computed: {
        ...mapGetters({
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
        }),
        selectedParticipants() {
            return (this.element.open.targeting.users || [])
                .map(user => ({
                    ...user,
                    type: "user",
                    name: `${user.firstname} ${user.lastname}`,
                }))
                .concat(
                    this.element.open.targeting.localTeams.map(item => ({
                        ...item,
                        type: "team",
                    })) || [],
                );
        },
        participants() {
            return [
                ...this.usersSearchList.map(user => ({
                    _id: user._id,
                    type: "user",
                    firstname: user.firstname,
                    lastname: user.lastname,
                    name: user.firstname + " " + user.lastname,
                    desc: user.teamRole.title,
                    picture: user.picture,
                })),
                ...this.teamsSearchList.map(team => ({
                    _id: team._id,
                    type: "team",
                    name: team.name,
                    desc: this.$t("typeahead.team"),
                    picture: teamPicture,
                })),
            ];
        },
        filePlaceholder() {
            if (this.element.location.type === "awsupload") {
                return getFileNameFromAWSKey(this.element.location.key);
            }
            return "";
        },
        targetMode: {
            get: function() {
                return this.element.open.targeting.all ? "all" : "selectedParticipants";
            },
            set: function(newValue) {
                switch (newValue) {
                    case "all":
                        this.element.open.targeting.all = true;
                        break;
                    case "selectedParticipants":
                        this.element.open.targeting.all = false;
                        break;
                }
            },
        },
    },
    watch: {
        "element.location.url": {
            handler(newValue) {
                if (newValue) {
                    this.element.location.type = "link";
                    this.file = "";
                }
            },
        },
    },
    methods: {
        onFileChange(files) {
            if (!files.length) {
                this.element.location.type = "link";
                return;
            }

            const file = files[0];

            this.element.mimetype = file.type;

            this.element.location.type = "awsupload";
            this.element.location.url = "";
            this.$refs["input-url"].error = false;

            // Upload
            this.fileUploading = true;

            this.$store
                .dispatch({
                    type: "learning/storage/upload",
                    payload: {
                        elementType: this.element.type,
                        file,
                    },
                })
                .catch(() => {
                    this.element.location.type = "link";
                    this.element.location.key = "";
                    this.element.mimetype = "";
                })
                .finally(() => {
                    this.fileUploading = false;
                });
        },
        isValidURL(text) {
            return isURL(text);
        },
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },

        getAcceptFileTypes(type) {
            switch (type) {
                case "article":
                    return ".bmp,.gif,.jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.ppt,.pptx";
                case "video":
                    return ".mp4,.avi,.mpeg,.webm";
                default:
                    return "";
            }
        },

        deleteElement() {
            this.$emit("deleteElement");
        },
        cancel() {
            this.$emit("cancel");
        },
        submit() {
            if (this.isValid()) {
                this.$emit("submit", this.element);
            }
        },
        typeaheadParticipantsOnSelect(participant) {
            this.selectedParticipants.push(participant);
            if (participant.type === "user") {
                this.element.open.targeting.users.push(participant);
            }
            if (participant.type === "team") {
                this.element.open.targeting.localTeams.push(participant);
            }

            this.$store.dispatch("users/search/reset");
            this.$store.dispatch("teams/search/reset");
        },
        typeaheadParticipantsOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
        removeSelectedParticipant(index) {
            const participant = this.selectedParticipants[index];
            this.selectedParticipants.splice(index, 1);
            if (participant.type === "user") {
                this.element.open.targeting.users = this.element.open.targeting.users.filter(
                    user => user._id !== participant._id,
                );
            }
            if (participant.type === "team") {
                this.element.open.targeting.localTeams = this.element.open.targeting.localTeams.filter(
                    team => team._id !== participant._id,
                );
            }
        },
    },
};
</script>

<style scoped>
.targeting {
    padding-left: 10px;
}
.footer {
    display: flex;
}
.footer div:first-of-type {
    flex-grow: 1;
}

.label {
    margin-right: 5px;
}
</style>
